import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../contexts/DarkModeContext';
import './ScientificCalculator.css';
import { FaCalculator, FaStar, FaTrophy, FaMedal } from 'react-icons/fa';

const ScientificCalculator = () => {
    const { isDarkMode } = useDarkMode();
    const [display, setDisplay] = useState('0');
    const [previousValue, setPreviousValue] = useState(null);
    const [operation, setOperation] = useState(null);
    const [waitingForOperand, setWaitingForOperand] = useState(false);
    const [isScientificMode, setIsScientificMode] = useState(false);
    const [points, setPoints] = useState(parseInt(localStorage.getItem('calculator-points') || '0'));
    const [level, setLevel] = useState(parseInt(localStorage.getItem('calculator-level') || '1'));
    const [calculationsCount, setCalculationsCount] = useState(parseInt(localStorage.getItem('calculator-calculations') || '0'));

    // حساب النقاط والمستوى
    const calculatePoints = () => {
        const newCalculations = calculationsCount + 1;
        setCalculationsCount(newCalculations);
        localStorage.setItem('calculator-calculations', newCalculations.toString());
        
        // 10 نقاط لكل عملية حسابية
        const newPoints = points + 10;
        setPoints(newPoints);
        localStorage.setItem('calculator-points', newPoints.toString());
        
        // رفع المستوى كل 100 نقطة
        const newLevel = Math.floor(newPoints / 100) + 1;
        if (newLevel > level) {
            setLevel(newLevel);
            localStorage.setItem('calculator-level', newLevel.toString());
        }
    };

    const inputDigit = (digit) => {
        if (waitingForOperand) {
            setDisplay(String(digit));
            setWaitingForOperand(false);
        } else {
            setDisplay(display === '0' ? String(digit) : display + digit);
        }
    };

    const inputDecimal = () => {
        if (waitingForOperand) {
            setDisplay('0.');
            setWaitingForOperand(false);
        } else if (display.indexOf('.') === -1) {
            setDisplay(display + '.');
        }
    };

    const clear = () => {
        setDisplay('0');
        setPreviousValue(null);
        setOperation(null);
        setWaitingForOperand(false);
    };

    const performOperation = (nextOperation) => {
        const inputValue = parseFloat(display);

        if (previousValue === null) {
            setPreviousValue(inputValue);
        } else if (operation) {
            const currentValue = previousValue || 0;
            const newValue = calculate(currentValue, inputValue, operation);
            setDisplay(String(newValue));
            setPreviousValue(newValue);
        }

        setWaitingForOperand(true);
        setOperation(nextOperation);
    };

    const calculate = (firstValue, secondValue, operation) => {
        switch (operation) {
            case '+': return firstValue + secondValue;
            case '-': return firstValue - secondValue;
            case '×': return firstValue * secondValue;
            case '÷': return firstValue / secondValue;
            case '^': return Math.pow(firstValue, secondValue);
            default: return secondValue;
        }
    };

    const scientificOperation = (operation) => {
        const inputValue = parseFloat(display);
        let result;

        switch (operation) {
            case 'sin':
                result = Math.sin(inputValue * Math.PI / 180);
                break;
            case 'cos':
                result = Math.cos(inputValue * Math.PI / 180);
                break;
            case 'tan':
                result = Math.tan(inputValue * Math.PI / 180);
                break;
            case 'log':
                result = Math.log10(inputValue);
                break;
            case 'ln':
                result = Math.log(inputValue);
                break;
            case 'sqrt':
                result = Math.sqrt(inputValue);
                break;
            case 'square':
                result = inputValue * inputValue;
                break;
            case 'factorial':
                result = factorial(inputValue);
                break;
            case 'inverse':
                result = 1 / inputValue;
                break;
            case 'abs':
                result = Math.abs(inputValue);
                break;
            default:
                return;
        }

        setDisplay(String(result));
        calculatePoints();
    };

    const factorial = (n) => {
        if (n < 0) return NaN;
        if (n === 0 || n === 1) return 1;
        let result = 1;
        for (let i = 2; i <= n; i++) {
            result *= i;
        }
        return result;
    };

    const handleEquals = () => {
        if (operation && previousValue !== null) {
            const inputValue = parseFloat(display);
            const newValue = calculate(previousValue, inputValue, operation);
            setDisplay(String(newValue));
            setPreviousValue(null);
            setOperation(null);
            setWaitingForOperand(true);
            calculatePoints();
        }
    };

    const getLevelTitle = (level) => {
        if (level <= 5) return 'مبتدئ';
        if (level <= 10) return 'متوسط';
        if (level <= 15) return 'متقدم';
        if (level <= 20) return 'خبير';
        return 'أسطورة';
    };

    const getLevelIcon = (level) => {
        if (level <= 5) return <FaStar />;
        if (level <= 10) return <FaMedal />;
        if (level <= 15) return <FaTrophy />;
        return <FaCalculator />;
    };

    return (
        <div className={`calculator-container ScientificCalculator ${isDarkMode ? 'dark' : 'light'}`}>
            <div className="calculator-header text-white">
                <h3 className='text-white mb-2'><FaCalculator /> الحاسبة العلمية</h3>
                <div className="calculator-stats text-white">
                    <div className="stat-item text-white">
                        <span className="stat-label text-white">النقاط:</span>
                        <span className="stat-value">{points}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">المستوى:</span>
                        <span className="stat-value">
                            {getLevelIcon(level)} {getLevelTitle(level)} ({level})
                        </span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label text-white">العمليات:</span>
                        <span className="stat-value">{calculationsCount}</span>
                    </div>
                </div>
            </div>

            <div className="calculator">
                <div className="calculator-display">
                    <div className="calculator-expression">
                        {previousValue !== null && operation && `${previousValue} ${operation}`}
                    </div>
                    <div className="calculator-input">{display}</div>
                </div>

                <div className="calculator-buttons">
                    {/* Scientific Functions */}
                    <div className="scientific-buttons">
                        <button 
                            className={`calc-btn scientific ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => scientificOperation('sin')}
                        >
                            sin
                        </button>
                        <button 
                            className={`calc-btn scientific ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => scientificOperation('cos')}
                        >
                            cos
                        </button>
                        <button 
                            className={`calc-btn scientific ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => scientificOperation('tan')}
                        >
                            tan
                        </button>
                        <button 
                            className={`calc-btn scientific ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => scientificOperation('log')}
                        >
                            log
                        </button>
                        <button 
                            className={`calc-btn scientific ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => scientificOperation('ln')}
                        >
                            ln
                        </button>
                        <button 
                            className={`calc-btn scientific ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => scientificOperation('sqrt')}
                        >
                            √
                        </button>
                        <button 
                            className={`calc-btn scientific ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => scientificOperation('square')}
                        >
                            x²
                        </button>
                        <button 
                            className={`calc-btn scientific ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => scientificOperation('factorial')}
                        >
                            x!
                        </button>
                        <button 
                            className={`calc-btn scientific ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => scientificOperation('inverse')}
                        >
                            1/x
                        </button>
                        <button 
                            className={`calc-btn scientific ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => scientificOperation('abs')}
                        >
                            |x|
                        </button>
                    </div>

                    {/* Standard Calculator */}
                    <div className="standard-buttons">
                        <button 
                            className={`calc-btn function ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={clear}
                        >
                            C
                        </button>
                        <button 
                            className={`calc-btn function ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => setDisplay(String(-parseFloat(display)))}
                        >
                            ±
                        </button>
                        <button 
                            className={`calc-btn function ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => setDisplay(String(parseFloat(display) / 100))}
                        >
                            %
                        </button>
                        <button 
                            className={`calc-btn operator ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => performOperation('÷')}
                        >
                            ÷
                        </button>

                        <button 
                            className={`calc-btn number ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => inputDigit(7)}
                        >
                            7
                        </button>
                        <button 
                            className={`calc-btn number ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => inputDigit(8)}
                        >
                            8
                        </button>
                        <button 
                            className={`calc-btn number ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => inputDigit(9)}
                        >
                            9
                        </button>
                        <button 
                            className={`calc-btn operator ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => performOperation('×')}
                        >
                            ×
                        </button>

                        <button 
                            className={`calc-btn number ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => inputDigit(4)}
                        >
                            4
                        </button>
                        <button 
                            className={`calc-btn number ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => inputDigit(5)}
                        >
                            5
                        </button>
                        <button 
                            className={`calc-btn number ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => inputDigit(6)}
                        >
                            6
                        </button>
                        <button 
                            className={`calc-btn operator ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => performOperation('-')}
                        >
                            -
                        </button>

                        <button 
                            className={`calc-btn number ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => inputDigit(1)}
                        >
                            1
                        </button>
                        <button 
                            className={`calc-btn number ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => inputDigit(2)}
                        >
                            2
                        </button>
                        <button 
                            className={`calc-btn number ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => inputDigit(3)}
                        >
                            3
                        </button>
                        <button 
                            className={`calc-btn operator ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => performOperation('+')}
                        >
                            +
                        </button>

                        <button 
                            className={`calc-btn number zero ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={() => inputDigit(0)}
                        >
                            0
                        </button>
                        <button 
                            className={`calc-btn number ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={inputDecimal}
                        >
                            .
                        </button>
                        <button 
                            className={`calc-btn operator ${isDarkMode ? 'dark' : 'light'}`}
                            onClick={handleEquals}
                        >
                            =
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScientificCalculator; 